import React from "react";
import { styled } from "@twilio-paste/core/styling-library";
import { Box } from "@twilio-paste/core/box";
import { Flex } from "@twilio-paste/core/flex";
import { Heading } from "@twilio-paste/core/heading";
import { Paragraph } from "@twilio-paste/core/paragraph";
import { ButtonGroup } from "@twilio-paste/core/button-group";
import { Button } from "@twilio-paste/core/button";
import { SupportIcon } from "@twilio-paste/icons/esm/SupportIcon";
import { VoiceCapableIcon } from "@twilio-paste/icons/esm/VoiceCapableIcon";
import { TourIcon } from "@twilio-paste/icons/esm/TourIcon";
import { UserIcon } from "@twilio-paste/icons/esm/UserIcon";
import { ProductMessagingIcon } from "@twilio-paste/icons/esm/ProductMessagingIcon";
import {
  IntelligentDiscoveryHomeBlock,
  NewChatFormValues,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";

import { constants, copy } from "../../definitions";
import { ChatFreeFormQuestionPopover } from "@twiliointernal/customer-deliverability-engine";

import { ReactComponent as ConversationalUseCase } from "../assets/conversationalUseCase.svg";
import { useWiseOwlContext } from "../../../../../hooks";

interface MessageErrorsPresentHomeProps {
  block: IntelligentDiscoveryHomeBlock;
}

const MessageErrorsPresentHomeContainer = styled(Box)`
  [data-paste-element="MESSAGE_STATUS_HEADER"] {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    margin-bottom: 16px;
  }
  [data-paste-element="MESSAGE_STATUS_COPY"] {
    line-height: 20px;
    margin-bottom: 0;
  }
  [data-paste-element="VIEW_ISSUES_BY"] {
    color: #606b85;
    margin-bottom: 16px;
  }
  [data-paste-element="BUTTON_GROUP_1"],
  [data-paste-element="BUTTON_GROUP_2"] {
    margin-bottom: 8px;
  }
  [data-paste-element="PHONE_NUMBERS_BTN"],
  [data-paste-element="SUB_ACCOUNTS_BTN"] {
    margin-left: 8px;
  }
`;

const MessageErrorsPresentHome: React.FC<MessageErrorsPresentHomeProps> = ({
  block,
}) => {
  const {
    handlers: { handleStartNewChat },
  } = useWiseOwlContext();
  const {
    values: { lastNDays, failedMessages, undeliveredMessages, totalMessages },
  } = block;

  const percentMessagesFailed = `${
    ((failedMessages + undeliveredMessages) / totalMessages) * 100
  }%`;

  const handleOnStartNewChat = (data: NewChatFormValues): void => {
    if (handleStartNewChat) {
      handleStartNewChat(data);
    }
  };

  return (
    <MessageErrorsPresentHomeContainer>
      {/* logo and message error copy */}
      <Box paddingBottom="space90">
        <Flex hAlignContent="center" marginBottom="space50">
          <ConversationalUseCase />
        </Flex>
        <Heading
          as="h4"
          variant="heading40"
          element={constants.MESSAGE_STATUS_HEADER}
        >
          {copy.messageErrorsPresent.headers.messageStatusHeader(lastNDays)}
        </Heading>
        <Paragraph element={constants.MESSAGE_STATUS_COPY}>
          {copy.messageErrorsPresent.messageStatusCopy(
            failedMessages,
            undeliveredMessages,
            percentMessagesFailed
          )}
        </Paragraph>
      </Box>
      {/* preformed questions pill */}
      <Box
        marginBottom="space90"
        data-testid="intelligent-assistant-prechat-error-state-pill-container"
      >
        <Heading as="h6" variant="heading60" element={constants.VIEW_ISSUES_BY}>
          {copy.messageErrorsPresent.headers.viewIssuesBy}
        </Heading>
        <ButtonGroup attached={false} element={constants.BUTTON_GROUP_1}>
          <Button
            variant="secondary"
            size="rounded_small"
            onClick={() =>
              handleOnStartNewChat({
                input:
                  "Can you give me a list of top error codes from my messagign account?",
              })
            }
            data-testid="intelligent-assistant-prechat-error-state-error-codes-btn"
          >
            <SupportIcon decorative={true} />
            Error codes
          </Button>
          <Button
            element={constants.PHONE_NUMBERS_BTN}
            variant="secondary"
            size="rounded_small"
            data-testid="intelligent-assistant-prechat-error-state-phone-numbers-btn"
            onClick={() =>
              handleOnStartNewChat({
                input:
                  "Can you give me a list of top phone numbers with message delivery errors?",
              })
            }
          >
            <VoiceCapableIcon decorative={true} />
            Phone numbers
          </Button>
        </ButtonGroup>
        <ButtonGroup attached={false} element={constants.BUTTON_GROUP_2}>
          <Button
            variant="secondary"
            size="rounded_small"
            data-testid="intelligent-assistant-prechat-error-state-countries-btn"
            onClick={() =>
              handleOnStartNewChat({
                input:
                  "Can you give me a list of top countries with message delivery errors?",
              })
            }
          >
            <TourIcon decorative={true} />
            Countries
          </Button>
          <Button
            variant="secondary"
            element={constants.SUB_ACCOUNTS_BTN}
            size="rounded_small"
            data-testid="intelligent-assistant-prechat-error-state-subaccounts-btn"
            onClick={() =>
              handleOnStartNewChat({
                input:
                  "Can you give me a list of top sub accounts with message delivery errors?",
              })
            }
          >
            <UserIcon decorative={true} />
            Subaccounts
          </Button>
        </ButtonGroup>
        <Button
          variant="secondary"
          size="rounded_small"
          data-testid="intelligent-assistant-prechat-error-state-message-btn"
          onClick={() =>
            handleOnStartNewChat({
              input: "I have a specific message to troubleshoot?",
            })
          }
        >
          <ProductMessagingIcon decorative={true} />
          {copy.messageErrorsPresent.callToAction.troubleshoot}
        </Button>
      </Box>
      <ChatFreeFormQuestionPopover />
    </MessageErrorsPresentHomeContainer>
  );
};

export default MessageErrorsPresentHome;
